import { Events } from '@shared/Events';
import { DateTimeSpan } from '@shared/ui-react/UtilComponents';
import { InlineEdit } from '@shared/ui-react/InlineEdit';
import { Notify } from '@shared/ui-native/Notify';
import { ReactUtils } from '@shared/ui-react/ReactUtils';
import { Dialog } from '@shared/ui-react/Dialog';
import { DocxViewer, PdfOrExternalViewer } from '@shared/documents/ui/DocumentViewers';
import { Button } from '@shared/ui-react/Button';
import { FilesService } from '@shared/services/FilesService';
import { cs } from '@shared/utils/utils-classname';
import { LStorage } from '@shared/Storage';
import { Ajax, Ajax2 } from '@shared/Ajax';
import { FileServiceClient } from '@shared/files/FileServiceClient';
import { isVideoExtension } from '@shared/utils/FileTypeUtils';

import { OrthoInfoDialog } from './OrthoInfoDialog';
import { PointCloudInfoDialog } from './PointCloudInfoDialog';

import './FileInfoDialog.scss';

// libs
import * as React from 'react';
import axios from 'axios';
import { observer } from 'mobx-react';
import { makeObservable, observable } from 'mobx';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { InputSwitch } from 'primereact/inputswitch';
import { ProgressSpinner } from 'primereact/progressspinner';
import { PresentableLabel } from '@shared/files/ui/PresentableLabel';
import { TabPane, Tabs } from '@shared/ui-react/Tabs';
import { FileHistory } from '@pages/files/dialogs/FileHistory';
import { FileComments } from '@shared/files/ui/FileComments';
import { ImagePanoViewer, ImageViewerInfo } from '@shared/ui-react/ImagePanoViewer';
import { DocumentPropertiesList } from '@pages/map/actions/DocumentPropertiesList';
import { WorkflowTaskHistory } from '@pages/tasks/WorkflowTaskHistory';

export const FileInfoDialog = {
    open: (fileUuid?: string, readOnly = false, showOtherTabs = false, queueUuids?: string[]) => {
        const targetEl = document.getElementById('react-portal-root');
        ReactUtils.safeRender(
            <Dialog onClose={FileInfoDialog.close} title={''} showHeader={false}>
                <FileInfoDialogComponent fileUuid={fileUuid} readOnly={readOnly} showOtherTabs={showOtherTabs} queueUuids={queueUuids} />
            </Dialog>,
            targetEl
        );
    },

    close: () => {
        ReactUtils.safeUnmountComponentAtNode(document.getElementById('react-portal-root'));
    },
};

@observer
class FileInfoDialogComponent extends React.Component<
    {
        fileUuid?: string;
        readOnly: boolean;
        showOtherTabs: boolean;
        queueUuids?: string[];
    },
    {}
> {
    @observable fileDetails: IFileDetails = null;
    @observable isLoading = false;
    @observable isReParsing = false;
    @observable isSettingPresentable = false;
    @observable collapseSidebar = false;
    private nameInlineEdit: InlineEdit = null;
    private inlineEditFile: File = null;

    constructor(props) {
        super(props);
        makeObservable(this);
    }

    componentDidMount() {
        this.fetch(this.props.fileUuid);
        if (this.props.queueUuids?.length) {
            window.addEventListener('keydown', this.handleKeyDown);
        }

        Events.on('file-updated', this.handleFileUpdated);
    }

    componentWillUnmount() {
        window.removeEventListener('keydown', this.handleKeyDown);
        Events.off('file-updated', this.handleFileUpdated);
    }

    handleFileUpdated = data => {
        if (data.newUuid && this.fileDetails) {
            const currentUuid = this.fileDetails.model?.uuid ?? this.fileDetails.image?.uuid;

            if (currentUuid === data.oldUuid) {
                setTimeout(() => {
                    this.fetch(data.newUuid);
                }, 500);
            }
        }
    };

    handleKeyDown = (e: KeyboardEvent) => {
        if (window.document.activeElement.tagName === 'INPUT' || window.document.activeElement.tagName === 'TEXTAREA') {
            return;
        }
        if (e.key === 'ArrowLeft') {
            this.goPrevious();
        }
        if (e.key === 'ArrowRight') {
            this.goNext();
        }
    };

    fetch = async (fileUuid: string) => {
        this.isLoading = true;
        try {
            try {
                this.fileDetails = await FilesService.getDetailsByUuid(fileUuid);
            } catch (e) {
                const model = await Ajax2({ url: `${kuura.getContextPath()}/api/1/document/external/${fileUuid}` });
                this.fileDetails = {
                    status: true,
                    model: {
                        version: 0,
                        filesize: 0,
                        ...model.document,
                    } as any,
                    parseLogs: [],
                    layers: [],
                    vehicles: [],
                };
            }
            if (this.canPreview) {
                this.collapseSidebar = LStorage.getOrDefault('file-info-collapse-sidebar', false);
            }
            this.isLoading = false;
        } catch {
            Notify.error();
        } finally {
            this.isLoading = false;
        }
    };

    render() {
        if (!this.fileDetails) {
            return <div className='overlay-loading' style={{ height: 150 }}></div>;
        }
        const { vehicles, parseLogs, layers } = this.fileDetails;
        const infoObject = this.fileDetails.model ?? this.fileDetails.image;
        const { id, uuid, name, version = 0, filesize, timestamp, description, deleted } = infoObject;
        const canEdit = !this.isExternal && kuura.hasPrivilege('PROJECT_W') && !deleted && !this.props.readOnly;
        const dlLink = this.isExternal ? null : location.origin + kuura.getContextPath() + `/api/1/file/${id}/get`;
        const fileContent = this.fileDetails.model ? kuuraModel.getLayerCountsText(this.fileDetails.model.layerCounts) : null;
        const lastModifiedUser = this.fileDetails.model?.lastModifiedUser ?? this.fileDetails.image?.creator?.username ?? null;
        const folderUuid = this.fileDetails.model?.folder?.uuid ?? this.fileDetails.model?.folderUuid ?? this.fileDetails.image.folderUuid;
        const canDownload = kuura.hasPrivilege('Download_Model');

        return (
            <>
                <div className='flex-column of-h'>
                    <header id='file-info__header'>
                        <InlineEdit
                            text={name}
                            url={`api/1/file/${uuid}/editFile.json`}
                            param='name'
                            disabled={!canEdit}
                            ref={el => (this.nameInlineEdit = el)}
                            onExit={() => {
                                this.inlineEditFile = null;
                                $('#file-info-wrapper').removeClass('fade-disable');
                            }}
                            onStartEdit={() => {
                                $('#file-info-wrapper').addClass('fade-disable');
                            }}
                            doSave={async name => {
                                let description = null;
                                if (document.querySelector('#file-edit-single-upload-desc') as HTMLInputElement) {
                                    description = (document.querySelector('#file-edit-single-upload-desc') as HTMLInputElement).checked ? infoObject?.description : null;
                                }

                                if (!this.inlineEditFile) {
                                    return Ajax2({
                                        url: kuura.getContextPath() + `/api/1/file/${uuid}/editFile.json`,
                                        method: 'POST',
                                        data: { name, description },
                                    });
                                } else {
                                    return new Promise(async (done, fail) => {
                                        try {
                                            const s3presignResult = await FileServiceClient.getS3UploadPath(this.inlineEditFile, {
                                                fileType: 'document',
                                                filename: name,
                                                description,
                                                folderUuid,
                                                headUuid: this.fileDetails.model ? this.fileDetails.model.headUuid : this.fileDetails.image.uuid,
                                            });
                                            await axios.put(s3presignResult.uploadUrl, this.inlineEditFile, {
                                                headers: {
                                                    'Content-Type': this.inlineEditFile.type,
                                                    'Content-MD5': s3presignResult.checksumMd5b64,
                                                },
                                            });
                                            done({
                                                status: true,
                                                fileId: id,
                                                ...(s3presignResult?.document ?? {}),
                                            });
                                        } catch (e) {
                                            fail(e);
                                            console.log('e:', e);
                                        }
                                    });
                                }
                            }}
                            onSaveSuccess={(text, resp) => {
                                console.log('file saved:', resp);
                                Events.trigger('tree-fetch');
                                Events.trigger('explorer_items_changed');
                                setTimeout(() => this.fetch(resp.uuid ?? resp.fileUuid ?? resp.updatedUuid), 1000);
                            }}
                        >
                            <div className='flex-column' style={{ marginTop: 10 }}>
                                {this.fileDetails?.model?.headUuid && (
                                    <>
                                        <input
                                            type='file'
                                            name='file'
                                            id='file-edit-single-upload'
                                            onChange={event => {
                                                try {
                                                    const filename = event.target.value.replace(/^.*[\\\/]/, '');
                                                    this.inlineEditFile = event.target.files[0];
                                                    this.nameInlineEdit.setText(filename);
                                                } catch (e) {
                                                    console.warn(e);
                                                }
                                            }}
                                        />

                                        {this.fileDetails?.model?.description?.trim()?.length > 0 && (
                                            <label style={{ marginTop: 10, marginBottom: 10 }}>
                                                <input type='checkbox' id='file-edit-single-upload-desc' defaultChecked={true} /> {__('js_files.keep_current_description')}
                                            </label>
                                        )}
                                    </>
                                )}
                            </div>
                        </InlineEdit>
                    </header>

                    <div className='infrakit-dialog__top-right'>
                        {this.canPreview && (
                            <>
                                {this.props.queueUuids?.length > 1 && (
                                    <>
                                        <span style={{ marginTop: -2 }}>
                                            {this.currentIndexInQueue + 1} / {this.props.queueUuids.length}
                                        </span>
                                        <Button disabled={this.isLoading} onClick={this.goPrevious} icon='iki iki-chevron-left' element='div' />
                                        <Button disabled={this.isLoading} onClick={this.goNext} icon='iki iki-chevron-right' element='div' />
                                    </>
                                )}
                                <Button onClick={this.toggleSidebar} tooltip={__(this.collapseSidebar ? 'general.show_details' : 'general.hide_details')} element='div'>
                                    <i className='iki iki-sort'></i>
                                </Button>
                                {this.isPdf && (
                                    <Button
                                        onClick={() => window.open(kuura.getContextPath() + '/api/1/file/' + infoObject.id + '/get?inline=true', '_blank').focus()}
                                        tooltip={__('js_files.open_pdf_in_new_tab')}
                                        icon='iki iki-fullscreen'
                                        element='div'
                                    />
                                )}
                            </>
                        )}
                        <div onClick={FileInfoDialog.close}>
                            <i className='iki iki-cross' />
                        </div>
                    </div>

                    <div className='flex-row of-h' style={{ padding: '0 15px 15px', gap: 15 }}>
                        {(!this.canPreview || (this.canPreview && !this.collapseSidebar)) && (
                            <div id='file-info-wrapper' className={cs({ 'overlay-loading': this.isLoading })}>
                                <Tabs displayValue='flex' flexDirection='column' lazyLoad={true} hideSingleTab={true}>
                                    <TabPane title={__('general.file_info')}>
                                        <aside className='flex-column gap-half of-h'>
                                            <div className='card'>
                                                <table id='file-info__table'>
                                                    <tbody>
                                                        <tr>
                                                            <td>{__('js_files.version')}</td>
                                                            <td>{version + 1}</td>
                                                        </tr>

                                                        {this.isExternal && (
                                                            <tr>
                                                                <td>URL</td>
                                                                <td>
                                                                    <div className='ellipsis' style={{ maxWidth: 300 }} title={this.fileDetails.model.url}>
                                                                        {this.fileDetails.model.url}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}

                                                        <tr>
                                                            <td>{__('js_files.file_size')}</td>
                                                            <td>{utils.bytesToLargestUnit(filesize)}</td>
                                                        </tr>

                                                        <tr>
                                                            <td>{__('general.creation_time')}</td>
                                                            <td>
                                                                <DateTimeSpan timestamp={timestamp} /> ({lastModifiedUser})
                                                            </td>
                                                        </tr>

                                                        <tr>
                                                            <td>{__('general.description')}</td>
                                                            <td className='file-info__description-cell'>
                                                                <InlineEdit
                                                                    text={description}
                                                                    url={`documents_bulk_edit.json`}
                                                                    param='description'
                                                                    minLength={0}
                                                                    disabled={!canEdit}
                                                                    doSave={async description => {
                                                                        return Ajax({
                                                                            url: 'documents_bulk_edit.json',
                                                                            method: 'POST',
                                                                            contentType: 'urlencoded',
                                                                            data: {
                                                                                documentIds: [id],
                                                                                description,
                                                                            },
                                                                        });
                                                                    }}
                                                                    onExit={() => null}
                                                                    onSaveSuccess={() => {
                                                                        infoObject.description = description;
                                                                        Events.trigger('explorer_items_changed');
                                                                    }}
                                                                ></InlineEdit>
                                                            </td>
                                                        </tr>

                                                        {this.canBePresentable && (
                                                            <tr>
                                                                <td>
                                                                    <div className='flex-row ai-center'>
                                                                        <PresentableLabel />
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <div className='flex-row ai-center'>
                                                                        <InputSwitch
                                                                            disabled={this.isSettingPresentable || !canEdit}
                                                                            checked={this.fileDetails.model?.presentable}
                                                                            onChange={e => this.setPresentable(e.value)}
                                                                        />
                                                                        {this.isSettingPresentable && (
                                                                            <ProgressSpinner style={{ width: 20, height: 20, marginLeft: 10 }} strokeWidth='4' />
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}

                                                        {canEdit && canDownload && dlLink && (
                                                            <tr>
                                                                <td>{__('js_files.file_link')}</td>
                                                                <td>
                                                                    <div className='flex-row ai-center'>
                                                                        <button
                                                                            onClick={() => (window.location.href = dlLink)}
                                                                            className='ik-button-small'
                                                                            style={{ marginRight: 10 }}
                                                                        >
                                                                            <i className='iki iki-download'></i>
                                                                            <span>{__('js_files.download_btn')}</span>
                                                                        </button>

                                                                        <CopyToClipboard text={dlLink} onCopy={() => Notify.success('Copied')}>
                                                                            <button className='ik-button-small'>{__('js_map.copy_to_clipboard')}</button>
                                                                        </CopyToClipboard>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}

                                                        {fileContent?.length > 0 && (
                                                            <tr>
                                                                <td>{__('js_files.file_contents')}</td>
                                                                <td>{fileContent}</td>
                                                            </tr>
                                                        )}

                                                        {canEdit && !this.isPhoto && (
                                                            <tr>
                                                                <td></td>
                                                                <td>
                                                                    <Button className='ik-button-small' loading={this.isReParsing} onClick={this.reparse}>
                                                                        {__('js_files.reparse')}
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                            {layers && layers.length > 0 && (
                                                <div className='card of-a'>
                                                    <FileInfoLayers layers={layers} />
                                                </div>
                                            )}
                                            {!this.isPointCloud && parseLogs && parseLogs.length > 0 && (
                                                <div className='card of-a'>
                                                    <FileInfoParseLogs parseLogs={parseLogs} />
                                                </div>
                                            )}
                                            {vehicles && vehicles.length > 0 && (
                                                <div className='card of-a'>
                                                    <FileInfoVehicles vehicles={vehicles} />
                                                </div>
                                            )}
                                            {this.isOrtho && <OrthoInfoDialog project_uuid={kuura.getProjectUuid()} file_uuid={this.fileDetails.model.uuid} />}
                                            {this.isPointCloud && <PointCloudInfoDialog project_uuid={kuura.getProjectUuid()} file_uuid={this.fileDetails.model.uuid} />}
                                        </aside>
                                    </TabPane>

                                    {this.props.showOtherTabs && (
                                        <TabPane title={__('general.versions')}>
                                            <div className='card padded-half of-a'>
                                                <FileHistory fileId={infoObject.id} readOnly={true} />
                                            </div>
                                        </TabPane>
                                    )}

                                    {this.props.showOtherTabs && (
                                        <TabPane title={__('general.comments')}>
                                            <div className='card padded-half of-a'>
                                                <FileComments fileId={infoObject.id} readOnly={true} />
                                            </div>
                                        </TabPane>
                                    )}
                                </Tabs>
                            </div>
                        )}
                        {this.isPdfOrExternal && (
                            <div>
                                <PdfOrExternalViewer
                                    idocument={this.fileDetails.model as any}
                                    height={'calc(100vh - 250px)'}
                                    width={this.collapseSidebar ? '90vw' : '60vw'}
                                    infoPosition={null}
                                    showFillSuccessMessage={false}
                                    onOpenDocumentUuid={uuid => {
                                        Events.trigger('explorer_items_changed');
                                        this.fetch(uuid);
                                    }}
                                />
                            </div>
                        )}
                        {this.isDocx && (
                            <div style={{ overflow: 'auto', minWidth: 500 }}>
                                <DocxViewer document={this.fileDetails.model as any} />
                            </div>
                        )}

                        {this.isPhoto && (
                            <div style={{ display: 'flex', overflow: 'auto', minWidth: 500, height: 'calc(100vh - 250px)', width: this.collapseSidebar ? '90vw' : '60vw' }}>
                                <ImagePanoViewer image={this.fileDetails.image} showInfo={false} getAllPanoramas={null} isMobile={false} openImageId={id => null} />
                            </div>
                        )}
                    </div>
                </div>
            </>
        );
    }

    reparse = async () => {
        this.isReParsing = true;
        try {
            if (this.isOrtho) {
                console.log('reparsing ortho');
                await this.reparseOrtho();
            } else if (this.isPointCloud) {
                console.log('reparsing point cloud');
                await this.reparsePointCloud();
            } else {
                console.log('reparsing file');
                await FilesService.clearDocCache([this.fileDetails.model.uuid]);
                await FilesService.reparseFiles([this.fileDetails.model.uuid]);
                this.fetch(this.fileDetails.model.uuid);
            }
        } catch {
            Notify.error();
        } finally {
            this.isReParsing = false;
        }
    };

    reparseOrtho = async () => {
        const domain = kuura.AwsApiGatewayDomain();
        const path = 'reality-capture/v1/raster/reparse';
        const url = new URL(path, domain).toString();
        const body = {
            project_uuid: kuura.getProjectUuid(),
            file_uuid: this.fileDetails.model.uuid,
        };
        return await Ajax2({ url: url, data: body, useToken: true, method: 'POST', contentType: 'json' });
    };

    reparsePointCloud = async () => {
        const domain = kuura.AwsApiGatewayDomain();
        const path = 'reality-capture/v1/pointcloud/reparse';
        const url = new URL(path, domain).toString();
        const body = {
            project_uuid: kuura.getProjectUuid(),
            file_uuid: this.fileDetails.model.uuid,
        };
        return await Ajax2({ url: url, data: body, useToken: true, method: 'POST', contentType: 'json' });
    };

    toggleSidebar = () => {
        this.collapseSidebar = !this.collapseSidebar;
        LStorage.setItem('file-info-collapse-sidebar', this.collapseSidebar);
    };

    setPresentable = (presentable: boolean) => {
        this.isSettingPresentable = true;
        Ajax({
            url: 'documents_bulk_edit.json',
            method: 'POST',
            contentType: 'urlencoded',
            data: {
                documentIds: [this.fileDetails.model.id],
                presentable: Boolean(presentable),
            },
        })
            .fail(Notify.error)
            .done(() => {
                this.fileDetails.model.presentable = Boolean(presentable);
                Events.trigger('explorer_items_changed');
            })
            .always(() => {
                this.isSettingPresentable = false;
            });
    };

    goPrevious = () => {
        if (this.isLoading) return;
        const newIndex = this.currentIndexInQueue - 1 < 0 ? this.props.queueUuids.length - 1 : this.currentIndexInQueue - 1;
        this.fetch(this.props.queueUuids[newIndex]);
    };

    goNext = () => {
        if (this.isLoading) return;
        const newIndex = this.currentIndexInQueue + 1 > this.props.queueUuids.length - 1 ? 0 : this.currentIndexInQueue + 1;
        this.fetch(this.props.queueUuids[newIndex]);
    };

    get currentIndexInQueue() {
        return this.props.queueUuids.indexOf(this.fileDetails.model?.uuid ?? this.fileDetails.image?.uuid);
    }

    get canPreview() {
        return this.isDocx || this.isPdfOrExternal || this.isPhoto || this.isVideo;
    }

    get isPdf() {
        if (!this.fileDetails.model) return false;
        return utils.getExtensionFromFilename(this.fileDetails.model.name).toLowerCase() === 'pdf';
    }

    get isPdfOrExternal() {
        return this.isPdf || this.isTxt || this.isExternal || this.isVideo;
    }
    get isTxt() {
        if (!this.fileDetails.model) return false;
        return utils.getExtensionFromFilename(this.fileDetails.model.name).toLowerCase() === 'txt';
    }

    get isVideo() {
        if (!this.fileDetails.model) return false;
        const ext = utils.getExtensionFromFilename(this.fileDetails.model.name).toLowerCase();
        return isVideoExtension(ext);
    }

    get isExternal() {
        if (!this.fileDetails.model) return false;
        return this.fileDetails.model.hasOwnProperty('url');
    }

    get isDocx() {
        if (!this.fileDetails.model) return false;
        return ['docx'].includes(utils.getExtensionFromFilename(this.fileDetails.model.name).toLowerCase());
    }

    get isPhoto() {
        return !!this.fileDetails.image;
    }

    get isOrtho() {
        if (!this.fileDetails.model) return false;
        return FilesService.isOrtho(this.fileDetails.model.name);
    }

    get isPointCloud() {
        if (!this.fileDetails.model) return false;
        return FilesService.isPointCloud(this.fileDetails.model.name);
    }

    get canBePresentable() {
        if (!this.fileDetails.model) return false;
        return this.fileDetails.model && !this.isExternal;
    }
}

@observer
class PhotoExplorerComponent extends React.Component<{
    photo: any;
    allPhotos?: any[];
}> {
    viewer: any = null;

    render() {
        const photo = this.props.photo;

        return (
            <div id='image-popup-wrapper' className={photo.panorama ? 'panorama' : null}>
                <ImagePanoViewer showInfo={false} image={photo} getAllPanoramas={null} isMobile={false} openImageId={id => null} ref={instance => (this.viewer = instance)} />

                <div style={{ overflow: 'auto', minHeight: 150 }}>
                    <ImageViewerInfo image={photo} position='bottom' />

                    <div style={{ textAlign: 'left', margin: '0 0 5px 15px' }}>
                        <DocumentPropertiesList doc={photo} canEdit={true} />
                    </div>
                </div>
            </div>
        );
    }
}

export let FileInfoParseLogs: React.FC<{ parseLogs: IParseLog[] }> = ({ parseLogs }) => (
    <div id='file-info__parse-log-wrapper' style={{ marginTop: 10 }}>
        {[...parseLogs]
            .sort((a, b) => {
                const aLvl = a.level === 'ERROR' ? 3 : a.level === 'WARNING' ? 2 : 1;
                const bLvl = b.level === 'ERROR' ? 3 : b.level === 'WARNING' ? 2 : 1;
                return bLvl - aLvl;
            })
            .map((item, i) => (
                <div className={'file-info__parse-log-item parse-log--' + item.level} key={i}>
                    <span>{item.level}</span> - {item.messageKey === 'parser_error' ? kuura.getMessage('js_files.parsing.parser_error') : kuura.getMessage(item.messageKey)}
                    {item.arguments?.length > 0 && <span>: {item.arguments.filter(Boolean).join(' , ')}</span>}
                </div>
            ))}
    </div>
);

export let FileInfoVehicles: React.FC<{ vehicles: IVehicle[] }> = ({ vehicles }) => (
    <div style={{ display: vehicles.length > 0 ? 'block' : 'none', marginTop: 10 }}>
        <div id='file-info__vehicles-wrapper'>
            <h5>{__('js_files.assigned_to_vehicles')}:</h5>
            {vehicles.map(vehicle => (
                <a className='file-info__vehicles-item' key={vehicle.id} href={'vehicles.htm#/overview/' + vehicle.id}>
                    {vehicle.name}
                </a>
            ))}
        </div>
    </div>
);

export let FileInfoLayers: React.FC<{ layers: ILayer[] }> = ({ layers }) => (
    <div id='file-info__layers-wrapper' style={{ marginTop: 10 }}>
        <table className='simple-table'>
            <thead>
                <tr>
                    <th>{__('js_files.layer.layer_type')}</th>
                    <th>{__('js_files.layer.name')}</th>
                    <th>{__('js_files.layer.code')}</th>
                    <th>{__('js_files.layer.surface_code')}</th>
                    <th>{__('js_files.layer.terrain_code')}</th>
                </tr>
            </thead>

            <tbody>
                {layers.map((layer, i) => (
                    <tr key={i}>
                        <td>{layer.layerType}</td>
                        <td>{layer.name}</td>
                        <td>{layer.code}</td>
                        <td>{layer.surfaceCode}</td>
                        <td>{layer.terrainCode}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);
